import { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Checkbox, createFilterOptions, InputAdornment, TextField } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader } from '../../../atomic/molecules';
import OmnichannelQuery from '../../../services/query/omnichannel/Omnichannel.services';
import PostCheckoutQuery from '../../../services/query/postCheckout/PostCheckoutQuery.service';
import StoreCargoBlockingCommand from '../../../services/command/storeCargoBlocking/StoreCargoBlockingCommand.services';
import { SCB, scbCls } from '../storeCargoBlocking.classes';

interface Option {
    id: number;
    name: string;
}

interface StoreOption {
    code: string;
    name: string;
}

interface SearchDrawerProps {
    isDrawerOpen: boolean;
    handleDrawerClose: () => void;
    handleSearch?: (filterParams: any) => void;
    onAddSuccess?: (filterParams: any) => void;
    mode?: 'search' | 'add';
    cargoCompanyOptions?: Option[];
    storeOptions?: StoreOption[];
    setIsLoading?: (loading: boolean) => void;
    isLoading?: boolean;
}

interface FormState {
    selectedCargoCompany: Option | null;
    selectedStores: StoreOption[];
}

const ALL_OPTION_ID = -1;
const ALL_STORE_CODE = '-1';
const DRAWER_WIDTH = '480px';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const INITIAL_FORM_STATE: FormState = {
    selectedCargoCompany: null,
    selectedStores: [],
};

const filterOptions = createFilterOptions<StoreOption>({
    matchFrom: 'any',
    stringify: (option) => `${option.name} ${option.code}`,
});

const AUTOCOMPLETE_SINGLE_SX = {
    '& .MuiAutocomplete-inputRoot': { minHeight: '50px' },
};

const AUTOCOMPLETE_MULTI_SX = {
    '& .MuiAutocomplete-inputRoot': {
        minHeight: '50px',
        flexWrap: 'wrap',
        maxHeight: '120px',
    },
};

const extractStoreOptions = (res: any): StoreOption[] => {
    const stores = res?.data || res?.stores || [];
    if (!Array.isArray(stores)) {
        return [];
    }

    return stores
        .filter((item: any) => item?.storeCode && item?.storeName)
        .map((item: any) => ({
            code: item.storeCode,
            name: item.storeName,
        }))
        .sort((a: StoreOption, b: StoreOption) => a.name.localeCompare(b.name));
};

const buildAddResultSwalHtml = (
    t: (key: string) => string,
    totalRequested: number,
    successCount: number,
    failedCount: number,
    message: string
) => `
    <div style="display: flex; justify-content: center; gap: 24px; margin-bottom: 16px;">
        <div style="text-align: center;">
            <div style="font-size: 28px; font-weight: bold; color: #2196F3;">${totalRequested}</div>
            <div style="font-size: 12px; color: #666;">${t('storeCargoBlocking.totalRequested')}</div>
        </div>
        <div style="text-align: center;">
            <div style="font-size: 28px; font-weight: bold; color: #4CAF50;">${successCount}</div>
            <div style="font-size: 12px; color: #666;">${t('storeCargoBlocking.successCount')}</div>
        </div>
        <div style="text-align: center;">
            <div style="font-size: 28px; font-weight: bold; color: #F44336;">${failedCount}</div>
            <div style="font-size: 12px; color: #666;">${t('storeCargoBlocking.failedCount')}</div>
        </div>
    </div>
    ${message ? `<div style="margin-top: 12px; padding: 12px; background-color: #f5f5f5; border-radius: 8px; font-size: 13px; color: #333; text-align: left; max-height: 150px; overflow-y: auto;">${message}</div>` : ''}
`;

const getUpsertErrorMessage = (response: any, fallback: string) => {
    if (response?.message) {
        return response.message;
    }

    const failedReasons = (response?.failedItems || [])
        .map((item: any) => item?.reason)
        .filter((reason: string | null | undefined) => !!reason);

    if (failedReasons.length > 0) {
        return failedReasons.join('\n');
    }

    return fallback;
};

export const SearchDrawer = ({
    isDrawerOpen,
    handleDrawerClose,
    handleSearch,
    onAddSuccess,
    mode = 'search',
    cargoCompanyOptions: cargoCompanyOptionsProp = [],
    storeOptions: storeOptionsProp = [],
    setIsLoading,
    isLoading = false,
}: SearchDrawerProps) => {
    const { t } = useTranslation();

    const allOption: Option = useMemo(() => ({ id: ALL_OPTION_ID, name: t('general.all') ?? 'Tümü' }), [t]);
    const allStoreOption: StoreOption = useMemo(
        () => ({ code: ALL_STORE_CODE, name: t('general.all') ?? 'Tümü' }),
        [t]
    );

    const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
    const [cargoCompanyOptions, setCargoCompanyOptions] = useState<Option[]>([]);
    const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (cargoCompanyOptionsProp?.length > 0) {
            setCargoCompanyOptions(cargoCompanyOptionsProp);
        }
    }, [cargoCompanyOptionsProp]);

    useEffect(() => {
        if (storeOptionsProp?.length > 0) {
            setStoreOptions(storeOptionsProp);
        }
    }, [storeOptionsProp]);

    useEffect(() => {
        if (isDrawerOpen && cargoCompanyOptions?.length === 0 && cargoCompanyOptionsProp?.length === 0) {
            setIsLoadingOptions(true);
            PostCheckoutQuery.GetShippingCompanies()
                .then((res: any) => {
                    if (res?.shippingCompanies && Array.isArray(res.shippingCompanies)) {
                        const options = res.shippingCompanies
                            .filter(
                                (company: any) =>
                                    company?.shippingCompanyId != null &&
                                    company?.name != null &&
                                    company?.isCargoCompany === true
                            )
                            .map((company: any) => ({
                                id: company.shippingCompanyId,
                                name: company.name ?? '',
                            }));
                        setCargoCompanyOptions(options);
                    }
                })
                .catch((err: any) => {
                    console.error('Error fetching cargo companies:', err);
                })
                .finally(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [isDrawerOpen, cargoCompanyOptions?.length, cargoCompanyOptionsProp?.length]);

    useEffect(() => {
        if (isDrawerOpen && storeOptions?.length === 0 && storeOptionsProp?.length === 0) {
            setIsLoadingOptions(true);
            OmnichannelQuery.StoreList()
                .then((res: any) => {
                    setStoreOptions(extractStoreOptions(res));
                })
                .catch((err: any) => {
                    console.error('Error fetching stores:', err);
                })
                .finally(() => {
                    setIsLoadingOptions(false);
                });
        }
    }, [isDrawerOpen, storeOptions?.length, storeOptionsProp?.length]);

    useEffect(() => {
        if (!isDrawerOpen) {
            setFormState(INITIAL_FORM_STATE);
        }
    }, [isDrawerOpen]);

    const isFormValid = useMemo(
        () =>
            formState.selectedCargoCompany !== null &&
            formState.selectedStores?.length > 0 &&
            (mode === 'search' || formState.selectedCargoCompany?.id > 0),
        [formState.selectedCargoCompany, formState.selectedStores, mode]
    );

    const handleSubmit = useCallback(() => {
        const cargoCompanyId =
            formState.selectedCargoCompany?.id === ALL_OPTION_ID ? ALL_OPTION_ID : formState.selectedCargoCompany?.id;

        const isAllStoresSelected = formState.selectedStores?.some((store) => store.code === ALL_STORE_CODE);
        const storeCodes = isAllStoresSelected
            ? [ALL_STORE_CODE]
            : formState.selectedStores?.map((store) => store.code);

        if (mode === 'search') {
            const filterParams = {
                cargoCompanyId,
                storeCodes,
            };
            handleSearch?.(filterParams);
            return;
        }

        setIsSubmitting(true);
        setIsLoading?.(true);

        const payload = {
            shippingCompanyId: cargoCompanyId,
            storeCodes,
        };

        StoreCargoBlockingCommand.UpsertBlockedStore(payload)
            .then((res: any) => {
                const response = res?.data || res || {};
                const {
                    isSuccess = false,
                    message = '',
                    totalRequested = 0,
                    successCount = 0,
                    failedCount = 0,
                } = response;
                const errorMessage = getUpsertErrorMessage(response, t('storeCargoBlocking.addError'));

                Swal.fire({
                    icon: isSuccess ? 'success' : failedCount > 0 && successCount > 0 ? 'warning' : 'error',
                    title: isSuccess ? t('general.success') : t('storeCargoBlocking.addResult'),
                    html: buildAddResultSwalHtml(
                        t,
                        totalRequested,
                        successCount,
                        failedCount,
                        isSuccess ? message : errorMessage
                    ),
                    confirmButtonText: t('general.ok'),
                    width: 450,
                });

                handleDrawerClose();

                if (successCount > 0) {
                    onAddSuccess?.({
                        cargoCompanyId,
                        storeCodes,
                    });
                }
            })
            .catch((err: any) => {
                console.error('Error adding blocked store:', err);
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('storeCargoBlocking.addError'),
                    confirmButtonText: t('general.ok'),
                });
            })
            .finally(() => {
                setIsSubmitting(false);
                setIsLoading?.(false);
            });
    }, [mode, formState, handleSearch, setIsLoading, t, handleDrawerClose, onAddSuccess]);

    const handleClear = useCallback(() => {
        setFormState(INITIAL_FORM_STATE);
    }, []);

    const handleCargoCompanyChange = useCallback((_: any, newValue: Option | null) => {
        setFormState((prev) => ({ ...prev, selectedCargoCompany: newValue }));
    }, []);

    const handleStoreChange = useCallback(
        (_: any, newValue: StoreOption[]) => {
            const hasAllOption = newValue.some((store) => store.code === ALL_STORE_CODE);
            const hadAllOption = formState.selectedStores?.some((store) => store.code === ALL_STORE_CODE);

            if (hasAllOption && !hadAllOption) {
                setFormState((prev) => ({ ...prev, selectedStores: [allStoreOption] }));
            } else if (hasAllOption && newValue?.length > 1) {
                setFormState((prev) => ({
                    ...prev,
                    selectedStores: newValue?.filter((store) => store.code !== ALL_STORE_CODE),
                }));
            } else {
                setFormState((prev) => ({ ...prev, selectedStores: newValue }));
            }
        },
        [formState.selectedStores, allStoreOption]
    );

    const cargoCompanyOptionsWithAll = useMemo(
        () => (mode === 'search' ? [allOption, ...cargoCompanyOptions] : cargoCompanyOptions),
        [mode, allOption, cargoCompanyOptions]
    );

    const storeOptionsWithAll = useMemo(
        () => (mode === 'search' ? [allStoreOption, ...storeOptions] : storeOptions),
        [mode, allStoreOption, storeOptions]
    );

    const drawerTitle = useMemo(
        () => (mode === 'search' ? t('storeCargoBlocking.searchDrawerTitle') : t('storeCargoBlocking.addDrawerTitle')),
        [mode, t]
    );

    const submitButtonText = useMemo(() => (mode === 'search' ? t('general.search') : t('general.save')), [mode, t]);

    const isButtonsDisabled = isLoadingOptions || isSubmitting || isLoading;
    const showLoadingIndicator = isLoadingOptions || isSubmitting || isLoading;

    const isOptionEqualToValue = useCallback(
        (option: StoreOption, value: StoreOption) => option.code === value.code,
        []
    );
    const getStoreOptionLabel = useCallback(
        (option: StoreOption) => (option.code !== ALL_STORE_CODE ? `${option.code} - ${option.name}` : option.name),
        []
    );
    const getCargoOptionLabel = useCallback((option: Option) => option.name, []);
    const isCargoOptionEqualToValue = useCallback((option: Option, value: Option) => option.id === value.id, []);

    const renderSearchInput = (params: any, label: string, placeholder: string, fieldClass: string) => (
        <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            className={scbCls(SCB.input, SCB.inputTextfield, SCB.inputAutocomplete, fieldClass, params.className)}
            slotProps={{
                input: {
                    ...params.InputProps,
                    startAdornment: (
                        <>
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                        </>
                    ),
                },
            }}
        />
    );

    return (
        <SCUIGeneralDrawer width={DRAWER_WIDTH} open={isDrawerOpen} onClose={handleDrawerClose}>
            <SCUIGeneralDrawerHeader header={drawerTitle} onClose={handleDrawerClose} />
            {showLoadingIndicator && <SCUILoadingLinear display={showLoadingIndicator} />}
            <Box
                className={scbCls(SCB.drawer, mode === 'search' ? SCB.drawerSearch : SCB.drawerAdd, SCB.drawerBody)}
                sx={{ padding: '16px', display: 'flex', flexDirection: 'column' }}
            >
                <Box
                    className={SCB.drawerForm}
                    sx={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                    <Autocomplete
                        className={scbCls(SCB.input, SCB.inputAutocomplete, SCB.fieldCargoCompany)}
                        fullWidth
                        options={cargoCompanyOptionsWithAll}
                        getOptionLabel={getCargoOptionLabel}
                        value={formState.selectedCargoCompany}
                        onChange={handleCargoCompanyChange}
                        loading={isLoadingOptions}
                        disabled={isButtonsDisabled}
                        isOptionEqualToValue={isCargoOptionEqualToValue}
                        sx={AUTOCOMPLETE_SINGLE_SX}
                        renderOption={(props, option) => (
                            <li {...props} key={`cargo-company-${option.id}`}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                `${t('storeCargoBlocking.cargoCompany')} (*)`,
                                t('general.search') ?? '',
                                SCB.fieldCargoCompany
                            )
                        }
                    />

                    <Autocomplete
                        className={scbCls(SCB.input, SCB.inputAutocomplete, SCB.fieldStoreName)}
                        multiple
                        fullWidth
                        limitTags={1}
                        options={storeOptionsWithAll}
                        getOptionLabel={getStoreOptionLabel}
                        value={formState.selectedStores}
                        onChange={handleStoreChange}
                        loading={isLoadingOptions}
                        disabled={isButtonsDisabled}
                        isOptionEqualToValue={isOptionEqualToValue}
                        filterOptions={filterOptions}
                        disableCloseOnSelect
                        sx={AUTOCOMPLETE_MULTI_SX}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={`store-${option.code}`}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                    className={scbCls(SCB.input, SCB.inputCheckbox, SCB.cbStoreOption)}
                                />
                                {getStoreOptionLabel(option)}
                            </li>
                        )}
                        renderInput={(params) =>
                            renderSearchInput(
                                params,
                                `${t('storeCargoBlocking.storeName')} (*)`,
                                formState.selectedStores.length === 0 ? (t('general.search') ?? '') : '',
                                SCB.fieldStoreName
                            )
                        }
                    />
                </Box>

                <Box
                    className={SCB.drawerActions}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        paddingTop: '32px',
                        padding: '12px',
                    }}
                >
                    <SCUIButton
                        text={t('general.clear')}
                        onClick={handleClear}
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                        disabled={isButtonsDisabled}
                        className={scbCls(SCB.input, SCB.inputButton, SCB.btnDrawerClear)}
                    />
                    <SCUIButton
                        text={submitButtonText}
                        type="primary"
                        sizeType="fixed"
                        disabled={!isFormValid || isButtonsDisabled}
                        size={'l'}
                        buttonType="button"
                        onClick={handleSubmit}
                        className={scbCls(SCB.input, SCB.inputButton, SCB.btnDrawerSubmit)}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};
